/**
 * Squarified treemap layout (Bruls, Huizing & van Wijk 2000) — pure geometry, no
 * React. Given a list of weighted items and a box, it partitions the box into
 * tiles whose areas are proportional to the weights, laying items out in rows
 * along the shorter free side and greedily minimizing the worst tile aspect
 * ratio so tiles stay as close to square as possible.
 *
 * Used two ways by {@link StockTreemap}: once to place the sector regions, then
 * once per region to place that sector's stocks inside it.
 */

/** One weighted input cell. `value` drives tile area; non-positive values drop out. */
export interface TreemapItem {
  key: string;
  value: number;
}

/** One placed tile, in the same coordinate space as the input `width`/`height`. */
export interface TreemapTile {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface ScaledNode {
  key: string;
  area: number;
}

interface FreeRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Worst (largest) aspect ratio a row of already-scaled areas would have if laid
 * along a strip of length `side`. Standard squarify cost: compares the row's
 * biggest and smallest tile against the strip, so a value nearer 1 is squarer.
 */
function worstRatio(row: ScaledNode[], side: number): number {
  if (row.length === 0 || side <= 0) return Number.POSITIVE_INFINITY;
  let sum = 0;
  let max = 0;
  let min = Number.POSITIVE_INFINITY;
  for (const n of row) {
    sum += n.area;
    if (n.area > max) max = n.area;
    if (n.area < min) min = n.area;
  }
  if (sum <= 0) return Number.POSITIVE_INFINITY;
  const side2 = side * side;
  const sum2 = sum * sum;
  return Math.max((side2 * max) / sum2, sum2 / (side2 * min));
}

/** Place a finished row along the free rect's shorter side and shrink the rect. */
function placeRow(row: ScaledNode[], free: FreeRect, out: TreemapTile[]): void {
  const sum = row.reduce((s, n) => s + n.area, 0);
  if (sum <= 0) return;
  if (free.w <= free.h) {
    // Horizontal row pinned to the top; its height is the strip thickness.
    const rowH = Math.min(sum / free.w, free.h);
    let x = free.x;
    for (const n of row) {
      const tileW = rowH > 0 ? n.area / rowH : 0;
      out.push({ key: n.key, x, y: free.y, w: tileW, h: rowH });
      x += tileW;
    }
    free.y += rowH;
    free.h -= rowH;
  } else {
    // Vertical row pinned to the left; its width is the strip thickness.
    const rowW = Math.min(sum / free.h, free.w);
    let y = free.y;
    for (const n of row) {
      const tileH = rowW > 0 ? n.area / rowW : 0;
      out.push({ key: n.key, x: free.x, y, w: rowW, h: tileH });
      y += tileH;
    }
    free.x += rowW;
    free.w -= rowW;
  }
}

/**
 * Lay out `items` inside a `width` × `height` box (origin at 0,0). Returns one
 * tile per positive-valued item, in descending-area order. Empty input, a
 * degenerate box, or all-zero values yield `[]`.
 */
export function layoutTreemap(
  items: readonly TreemapItem[],
  width: number,
  height: number,
): TreemapTile[] {
  if (width <= 0 || height <= 0) return [];

  const totalValue = items.reduce(
    (s, it) => (it.value > 0 && Number.isFinite(it.value) ? s + it.value : s),
    0,
  );
  if (totalValue <= 0) return [];

  const scale = (width * height) / totalValue;
  const nodes: ScaledNode[] = items
    .filter((it) => it.value > 0 && Number.isFinite(it.value))
    .map((it) => ({ key: it.key, area: it.value * scale }))
    .sort((a, b) => b.area - a.area);

  const out: TreemapTile[] = [];
  const free: FreeRect = { x: 0, y: 0, w: width, h: height };
  let row: ScaledNode[] = [];
  let i = 0;

  while (i < nodes.length) {
    const side = Math.min(free.w, free.h);
    const next = nodes[i]!;
    const currentWorst = row.length === 0 ? Number.POSITIVE_INFINITY : worstRatio(row, side);
    const nextWorst = worstRatio([...row, next], side);
    // Keep growing the row while it stays squarer (or equal); otherwise flush it
    // and start a fresh row against the newly-shrunk free rect.
    if (row.length === 0 || nextWorst <= currentWorst) {
      row.push(next);
      i += 1;
    } else {
      placeRow(row, free, out);
      row = [];
    }
  }
  if (row.length > 0) placeRow(row, free, out);

  return out;
}
